import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { AlertCircle, Copy, ExternalLink, Instagram } from "lucide-react"
import { isInstagramInAppBrowser, openCurrentPageOutsideInAppBrowser } from "../../utils/inAppBrowser"

export const InstagramInAppBrowserNotice: React.FC = () => {
  const { t } = useTranslation()

  const [copyFeedback, setCopyFeedback] = useState("")

  const [openFeedback, setOpenFeedback] = useState("")

  const isInstagramBrowser = useMemo(() => isInstagramInAppBrowser(), [])

  if (!isInstagramBrowser) {
    return null
  }

  const copyCurrentLink = async () => {
    const currentUrl = window.location.href

    if (!navigator.clipboard?.writeText) {
      setOpenFeedback("")

      setCopyFeedback(t("publicMenu:in_app_browser.copy_error"))
      return
    }

    try {
      await navigator.clipboard.writeText(currentUrl)
      setOpenFeedback("")

      setCopyFeedback(t("publicMenu:in_app_browser.copy_success"))
    } catch {
      setOpenFeedback("")

      setCopyFeedback(t("publicMenu:in_app_browser.copy_error"))
    }
  }

  const handleOpenExternally = () => {
    const opened = openCurrentPageOutsideInAppBrowser()

    setCopyFeedback("")

    setOpenFeedback(
      opened ? t("publicMenu:in_app_browser.opening_external") : t("publicMenu:in_app_browser.open_error"),
    )
  }

  return (
    <section className="mb-4 rounded-lg border-2 border-red-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-1 font-bold text-gray-800">
          <Instagram size={16} />
          <span className="text-[11px] font-bold uppercase tracking-wide">Instagram</span>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
          <AlertCircle size={18} />
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-extrabold text-gray-900">{t("publicMenu:in_app_browser.title")}</h3>

          <p className="mt-1 text-sm leading-relaxed text-gray-600">{t("publicMenu:in_app_browser.description")}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={handleOpenExternally}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-600"
          data-interaction-description-key="interactionDescriptions:publicMenu.components.instagramInAppBrowserNotice.button.handleOpenExternally"
        >
          <ExternalLink size={17} />
          <span>{t("publicMenu:in_app_browser.open_external")}</span>
        </button>

        <button
          type="button"
          onClick={copyCurrentLink}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gray-200 px-4 py-2.5 text-sm font-bold text-gray-800 transition-colors hover:bg-red-600"
          data-interaction-description-key="interactionDescriptions:publicMenu.components.instagramInAppBrowserNotice.button.copyCurrentLink"
        >
          <Copy size={17} />
          <span>{t("publicMenu:in_app_browser.copy_link")}</span>
        </button>
      </div>

      {(openFeedback || copyFeedback) && (
        <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2">
          <p className="text-xs font-medium text-gray-700">{openFeedback || copyFeedback}</p>
        </div>
      )}
    </section>
  )
}
